import { type ChangeEvent } from "react";
import {
  Text,
  Pagination,
  Loader,
  Box,
  Select,
} from "@cruk/cruk-react-components";
import { useRouter } from "next/compat/router";

import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { fwsUrSuperTeamTeamPages } from "@fwa/src/services/superTeamService";
import { useTracking } from "@fwa/src/hooks/useTracking";
import { queryAsString } from "@fwa/src/utils/urlUtils";

import { MemberTeamJoin } from "@fwa/src/components/MemberTeamJoin";

import sharedStyles from "@fwa/src/components/styles.module.css";

import type {
  TeamPageType,
  SuperTeamMemberSortType,
  FwsResponseData,
} from "@fwa/src/types";

export function SuperTeamTeamPages({
  teamId,
  itemsPerPage,
}: {
  teamId: string;
  itemsPerPage: number;
}) {
  const router = useRouter();
  const { trackError } = useTracking();
  const { team_pages_page: teamPagesPage, sort: sortQuery } =
    router?.query || {};
  const currentPage = parseInt(queryAsString(teamPagesPage) || "1", 10);
  const sort = (queryAsString(sortQuery) ||
    "amount") as SuperTeamMemberSortType;
  const url = teamId
    ? fwsUrSuperTeamTeamPages({
        teamId,
        currentPage,
        itemsPerPage,
        sort,
      })
    : null;
  const { data, error, totalItems } =
    useSwrGet<FwsResponseData<TeamPageType>>(url);
  const teamPages = data?.results || [];
  const firstItemNumber = Math.max(currentPage - 1, 0) * itemsPerPage + 1;

  // We are effectively storing page number in the query string
  const setPageInQueryString = (pageNumber: number) => {
    // pathname isn't going to cut the mustard because it's on the teams page with a [slug]
    // so we need to make the url look like how it should do which is annoying because this list should be
    // agnostic to the page path however it still needs to know how to amend the query string in the URL
    router
      ?.push(
        {
          pathname: router.pathname,
          query: { ...router.query, team_pages_page: pageNumber },
        },
        undefined,
        { shallow: true },
      )
      .catch((err: Error) => {
        trackError(err, {
          component: "SuperTeamTeamPages",
          function: "setPageInQueryString",
        });
      });
  };

  const setSortInQueryString = (sortValue: string) => {
    router
      ?.push(
        {
          pathname: router.pathname,
          query: { ...router.query, sort: sortValue },
        },
        undefined,
        { shallow: true },
      )
      .catch((err: Error) => {
        trackError(err, {
          component: "SuperTeamTeamPages",
          function: "setSortInQueryString",
        });
      });
  };

  const handleFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const sortValue = e.target.value as SuperTeamMemberSortType;
    setSortInQueryString(sortValue || null);
  };

  return (
    <>
      {/* Error State */}
      {error ? <Text as="span">Unable to get superteam team pages</Text> : null}

      {/* Loading State */}
      {!error && !data ? <Loader /> : null}

      {/* Empty State */}
      {!error && data && !teamPages.length ? (
        <Text as="span">
          There are currently no team pages for this superteam
        </Text>
      ) : null}

      {/* Data State */}
      {data && teamPages.length ? (
        <>
          <Box>
            <div className={sharedStyles["filter-wrapper"]}>
              <Select
                required
                value={sort}
                label="Sort Teams by"
                onChange={handleFilterChange}
                aria-controls="memberList"
                hideRequiredIndicationInLabel
              >
                <option value="alpha">A-Z</option>
                <option value="amount">amount raised</option>
                <option value="recent">recently joined</option>
              </Select>
            </div>
          </Box>
          <Box>
            <div className={sharedStyles["row-space-around"]}>
              <Text>{`Showing ${firstItemNumber} to ${
                firstItemNumber + teamPages.length - 1
              } of ${totalItems} Teams`}</Text>
            </div>
          </Box>
          <Box>
            <ul
              className={sharedStyles["member-list"]}
              id="memberList"
              aria-live="assertive"
            >
              {!error &&
                teamPages
                  .filter((teamPage: TeamPageType) => teamPage.uniqueId)
                  .map((teamPage: TeamPageType) => (
                    <li key={teamPage.uniqueId}>
                      <MemberTeamJoin teamPage={teamPage} />
                    </li>
                  ))}
            </ul>
          </Box>
          <Box>
            <Pagination
              aria-controls="memberList"
              current={currentPage}
              perPage={itemsPerPage}
              items={totalItems}
              hideLast={false}
              pagerCallback={(n: number) => {
                setPageInQueryString(n);
              }}
            />
          </Box>
        </>
      ) : null}
    </>
  );
}

export default SuperTeamTeamPages;
